import React, { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import styles from "./WeekDateStrip.module.css";

export interface WeekDateStripProps {
  weekDates: Date[];
  selectedDate: Date;
  /** YYYY-MM-DD keys of days in this week that have at least one scheduled
   *  dose or conditional medicine — drives the "has data" visual state. */
  datesWithData: Set<string>;
  onSelect: (date: Date) => void;
  onPrevWeek: () => void;
  onNextWeek: () => void;
}

// Matches MCarousel's own swipe threshold/technique for consistency.
const MIN_SWIPE_DISTANCE = 50;

const DAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function toDateKey(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

const WeekDateStrip: React.FC<WeekDateStripProps> = ({
  weekDates,
  selectedDate,
  datesWithData,
  onSelect,
  onPrevWeek,
  onNextWeek,
}) => {
  const today = new Date();
  // +1 while swiping to next week, -1 for previous — decides which side
  // the outgoing/incoming week slides toward, so the motion always reads
  // as "moving through time" rather than a generic fade.
  const [direction, setDirection] = useState(0);
  const touchStartX = React.useRef(0);
  const touchEndX = React.useRef(0);

  const onTouchStart = (e: React.TouchEvent) => {
    touchEndX.current = 0;
    touchStartX.current = e.targetTouches[0].clientX;
  };

  const onTouchMove = (e: React.TouchEvent) => {
    touchEndX.current = e.targetTouches[0].clientX;
  };

  const onTouchEnd = () => {
    if (!touchStartX.current || !touchEndX.current) return;
    const distance = touchStartX.current - touchEndX.current;
    if (distance > MIN_SWIPE_DISTANCE) {
      setDirection(1);
      onNextWeek();
    } else if (distance < -MIN_SWIPE_DISTANCE) {
      setDirection(-1);
      onPrevWeek();
    }
  };

  // First day of the week as a stable key — changes exactly when the week
  // changes, which is what triggers AnimatePresence's slide transition.
  const weekKey = toDateKey(weekDates[0] ?? today);

  return (
    <div className={styles.weekView}>
      <div
        className={styles.swipeArea}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        <AnimatePresence initial={false} custom={direction} mode="popLayout">
          <motion.div
            key={weekKey}
            custom={direction}
            initial={{ x: direction * 60, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: direction * -60, opacity: 0 }}
            transition={{ duration: 0.22, ease: "easeOut" }}
            className={styles.dateRow}
          >
            {weekDates.map((date) => {
              // Today always renders filled, even if a different day is
              // selected — "selected" only gets its own (outlined) look
              // when it isn't today.
              const isToday = isSameDay(date, today);
              const isSelected = isSameDay(date, selectedDate);
              const hasData = datesWithData.has(toDateKey(date));
              const stateClass = isToday
                ? styles.today
                : isSelected
                ? styles.selectedOutline
                : hasData
                ? styles.hasData
                : styles.noData;
              return (
                <button
                  type="button"
                  key={date.toISOString()}
                  className={`${styles.dateCard} ${stateClass}`}
                  onClick={() => onSelect(date)}
                >
                  <span className={styles.dayLabel}>
                    {DAY_LABELS[date.getDay()]}
                  </span>
                  <span className={styles.dayNumber}>{date.getDate()}</span>
                </button>
              );
            })}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
};

export default WeekDateStrip;
